import { formOptions } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ColumnDef } from '@tanstack/react-table';
import { Pencil, Plus, Trash } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import ConfirmModal from '~/components/ConfirmModal';
import DataTable from '~/components/DataTable';
import { UserTags } from '~/partials/Tags';
import TooltipButton from '~/partials/Tooltip';
import { Button } from '~/primitives/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '~/primitives/dialog';
import { ScrollArea } from '~/primitives/scroll-area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetFooter,
    SheetHeader,
    SheetTitle,
} from '~/primitives/sheet';
import { ModalProps } from '~/util';
import { levelNoteFormInput, levelNoteFormSchema } from '~/util/forms/level';
import { useApiMutation } from '~/util/hooks/useApiMutation';
import { useAppForm } from '~/util/hooks/useAppForm';
import { useList } from '~/util/hooks/useList';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import {
    createNoteMutation,
    deleteNoteMutation,
    updateNoteMutation,
} from '~/util/mutations/staff/levels';
import { levelNotesQuery } from '~/util/queries/public/levels';
import { LevelNote, levelNoteTypes } from '~/util/types/levels';

const Route = getRouteApi('/list/$levelid');

export const LevelNotes = () => {
    const list = useList();
    const { levelid } = Route.useParams();

    const localeDateString = useLocaleDateString();

    const [editModalOpen, setEditModalOpen] = useState(false);
    const data = useQuery(levelNotesQuery({ list, levelid }))?.data?.sort(
        (a, b) => {
            if (!a.timestamp) return -1;
            if (!b.timestamp) return 1;
            return (
                new Date(a.timestamp).getTime() -
                new Date(b.timestamp).getTime()
            );
        },
    );

    const columns: ColumnDef<LevelNote>[] = useMemo(
        () => [
            {
                accessorKey: 'timestamp',
                header: 'Date',
                cell: ({
                    row: {
                        original: { timestamp },
                    },
                }) => (timestamp ? localeDateString(timestamp) : 'N/A'),
            },
            {
                accessorKey: 'note_type',
                header: 'Type',
                cell: ({
                    row: {
                        original: { note_type },
                    },
                }) => note_type,
            },
            {
                accessorKey: 'note',
                header: 'Note',
                cell: ({
                    row: {
                        original: { note },
                    },
                }) => note,
            },
        ],
        [localeDateString],
    );

    return (
        <div className="mt-6 mr-4 ml-2 text-left">
            <div className="flex mb-2">
                <h2 className="transition-colors text-3xl font-medium px-0.5 hover:text-secondary-light">
                    Level Notes
                </h2>
                <Pencil
                    className="size-6 ml-auto hover:text-secondary-light transition-colors cursor-pointer"
                    onClick={() => setEditModalOpen(true)}
                />
            </div>

            {data?.length === 0 ? (
                'This level does not have any notes.'
            ) : (
                <DataTable
                    columns={columns}
                    data={data || []}
                    headerClassName={() =>
                        'clamp-[text-xs-sm-clamp] px-0.5 lg:p-2 bg-transparent hover:!bg-transparent'
                    }
                    cellClassName={() => 'px-0.5 py-2 lg:px-2'}
                    rowClassName={() => 'bg-transparent hover:!bg-transparent'}
                />
            )}

            <EditLevelNotesDialog
                open={editModalOpen}
                onOpenChange={setEditModalOpen}
            />
        </div>
    );
};

export const EditLevelNotesDialog = (props: ModalProps) => {
    const [selectedNote, setSelectedNote] = useState<LevelNote | null>(null);
    const [isNewSheetOpen, setIsNewSheetOpen] = useState(false);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const list = useList();

    const { levelid } = Route.useParams();

    const { data, isPending } = useQuery(levelNotesQuery({ list, levelid }));

    const localeDateString = useLocaleDateString();
    const deleteMutation = useApiMutation(deleteNoteMutation());

    const columns: ColumnDef<LevelNote>[] = useMemo(
        () => [
            {
                accessorKey: 'note_type',
                header: 'Type',
                cell: ({
                    row: {
                        original: { note_type },
                    },
                }) => note_type,
            },
            {
                accessorKey: 'note',
                header: 'Note',
                cell: ({
                    row: {
                        original: { note },
                    },
                }) => note,
            },
            {
                accessorKey: 'added_by.global_name',
                header: 'Added by',
                cell: ({
                    row: {
                        original: { added_by },
                    },
                }) => <UserTags showFullName highContrast item={added_by} />,
            },
            {
                accessorKey: 'timestamp',
                header: 'Date',
                cell: ({
                    row: {
                        original: { timestamp },
                    },
                }) => (timestamp ? localeDateString(timestamp) : 'N/A'),
            },
            {
                id: 'actions',
                cell: ({ row: { original: note } }) => {
                    return (
                        <div className="flex items-center gap-1">
                            <TooltipButton text="Edit Note" side="top">
                                <Button
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                    onClick={(e) => {
                                        setSelectedNote(note);
                                        setIsEditSheetOpen(true);
                                        e.stopPropagation();
                                    }}
                                >
                                    <Pencil className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                            <TooltipButton text="Delete Note" side="top">
                                <Button
                                    variant="destructive"
                                    className="h-8 w-8 p-0"
                                    onClick={(event) => {
                                        setSelectedNote(note);
                                        setIsConfirmModalOpen(true);
                                        event.stopPropagation();
                                    }}
                                >
                                    <Trash className="h-5 w-5 m-auto" />
                                </Button>
                            </TooltipButton>
                        </div>
                    );
                },
            },
        ],
        [localeDateString],
    );

    return (
        <Dialog {...props}>
            <DialogContent disableDialogScroll>
                <DialogHeader className="pl-3">
                    <DialogTitle>Notes</DialogTitle>
                    <DialogDescription>
                        Notes for this level. Notes can be used to provide
                        additional information about the level for reviewers to
                        see on the submissions review page, as well as public
                        notes.
                    </DialogDescription>
                </DialogHeader>
                <ScrollArea className="min-h-0 flex-1 mr-1 pr-3">
                    <Button
                        className="my-4"
                        onClick={() => setIsNewSheetOpen(true)}
                    >
                        <Plus /> Add Note
                    </Button>
                    <DataTable
                        isLoading={isPending}
                        columns={columns}
                        data={data ?? []}
                    />
                </ScrollArea>
                <ConfirmModal
                    open={isConfirmModalOpen}
                    onOpenChange={setIsConfirmModalOpen}
                    title="Delete Note"
                    text="Are you sure you want to delete this note? This action cannot be undone."
                    onConfirm={() => {
                        if (!selectedNote) return;
                        deleteMutation.mutate({
                            note_id: selectedNote.id,
                            levelid,
                            list,
                        });
                    }}
                />
                <NewNoteSheet
                    open={isNewSheetOpen}
                    onOpenChange={setIsNewSheetOpen}
                />
                <EditNoteSheet
                    note={selectedNote!}
                    open={isEditSheetOpen}
                    onOpenChange={setIsEditSheetOpen}
                />
            </DialogContent>
        </Dialog>
    );
};

export const NewNoteSheet: React.FC<ModalProps> = (modalProps) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...createNoteMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'add-note-staff',
            defaultValues: {} as levelNoteFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelNoteFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({ data: value, list, levelid });
            },
        }),
    );

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Create Note</SheetTitle>
                        <SheetDescription>
                            Add a new note for this level. Nerf/Buff dates will
                            be displayed on the level&apos;s records page.
                            Reviewer and other notes will only be visible to
                            staff on the submissions review page.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="add-note-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="note_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelNoteTypes}
                                        label="Note Type"
                                        required
                                        placeholder="Select Note Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="timestamp">
                                {(field) => (
                                    <field.DateTimeField
                                        label="Timestamp"
                                        labelNotes="The time at which the note should take effect"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="note">
                                {(field) => (
                                    <field.TextAreaField
                                        label="Note"
                                        placeholder="Content of the note"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Create Note"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};

export const EditNoteSheet: React.FC<{ note: LevelNote } & ModalProps> = ({
    note,
    ...modalProps
}) => {
    const list = useList();

    const { levelid } = Route.useParams();

    const mutation = useApiMutation({
        ...updateNoteMutation(),
        onSuccess: () => modalProps.onOpenChange?.(false),
    });

    const form = useAppForm(
        formOptions({
            formId: 'edit-note-staff',
            defaultValues: note as levelNoteFormInput,
            validators: {
                onChangeAsyncDebounceMs: 500,
                onChange: levelNoteFormSchema,
            },
            onSubmit: async ({ value }) => {
                mutation.mutate({
                    data: value,
                    list,
                    levelid,
                    note_id: note.id,
                });
            },
        }),
    );

    useEffect(() => {
        form.reset(note);
    }, [note, form]);

    return (
        <>
            <Sheet {...modalProps}>
                <SheetContent className="w-full sm:w-auto sm:min-w-150 sm:max-w-150 overflow-y-scroll">
                    <SheetHeader>
                        <SheetTitle>Edit Note</SheetTitle>
                        <SheetDescription>
                            Update this note&apos;s content and timestamp.
                        </SheetDescription>
                    </SheetHeader>

                    <form
                        id="edit-note-staff"
                        onSubmit={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            form.handleSubmit();
                        }}
                    >
                        <div className="flex flex-col justify-start items-start gap-x-6 gap-y-3 p-6 pt-6">
                            <form.AppField name="note_type">
                                {(field) => (
                                    <field.DropdownField
                                        options={levelNoteTypes}
                                        label="Note Type"
                                        required
                                        placeholder="Select Note Type"
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="timestamp">
                                {(field) => (
                                    <field.DateTimeField
                                        label="Timestamp"
                                        labelNotes="The time at which the note should take effect i.e. whenever the nerfdate/buffdate was released, if applicable."
                                    />
                                )}
                            </form.AppField>
                            <form.AppField name="note">
                                {(field) => (
                                    <field.TextAreaField
                                        label="Note"
                                        placeholder="Content of the note"
                                    />
                                )}
                            </form.AppField>
                        </div>

                        <SheetFooter className="sm:justify-center">
                            <form.AppForm>
                                <form.SubscribeButton
                                    label="Edit Note"
                                    classname="w-11/12"
                                />
                            </form.AppForm>
                        </SheetFooter>
                    </form>
                </SheetContent>
            </Sheet>
        </>
    );
};
